import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import { authActions, selectEmployeeInfo, selectJobInfo } from 'features/Auth/authSlice';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { selectAllowedScreens } from 'features/common/screenSlice';
import { selectWorkerStatus } from 'features/common/workerSlice';
import { STATUS_RESPONSE_PROGRESSING } from 'features/Delivery/constants';
import {
  deliveryListActions,
  selectAsyncCreationTaskStatus,
  selectIsLinkDisabled,
} from 'features/Delivery/deliverySlice';
import { MouseEvent, useEffect, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { SCREEN_BUSSINESS_TYPE } from 'utils';
import EditModeHeader from '../EditModeHeader/EditModeHeader';
import service, { DeliveryCheckStatusResponse } from './services';
import './style.scss';

export interface StatusHeaderProps {
  actionName?: 'back' | 'logout';
  onAction?: () => void;
}

function StatusHeader({ actionName = 'logout', onAction }: StatusHeaderProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);
  const employeeInfo = useAppSelector(selectEmployeeInfo);
  const employeeJobInfo = useAppSelector(selectJobInfo);
  const isLinkDisabledByDelivery = useAppSelector(selectIsLinkDisabled);
  const workerStatus = useAppSelector(selectWorkerStatus);
  const asyncCreationStatus = useAppSelector(selectAsyncCreationTaskStatus);
  const isAnyTaskProcessing =
    workerStatus === STATUS_RESPONSE_PROGRESSING ||
    asyncCreationStatus === 'started' ||
    asyncCreationStatus === 'progressing';
  const isLinkDisabled = isLinkDisabledByDelivery || isAnyTaskProcessing;

  // state
  const [allowedScreens, setAllowedScreens] = useState<Set<string>>(new Set());

  const handleActionClick = () => {
    if (actionName === 'logout') {
      dispatch(authActions.actionLogout());
    } else {
      onAction && onAction();
    }
  };

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (isLinkDisabled) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (isEditMode) {
      // If in reedit mode, disable link restrictions
      dispatch(deliveryListActions.updateIsLinkDisabled(false));
    } else {
      // Normal check for approved delivery
      service.checkApprovedDelivery().then((value: DeliveryCheckStatusResponse) => {
        dispatch(
          deliveryListActions.updateIsLinkDisabled(!(value.data.delivery_count === value.data.delivery_count_approved))
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLinkDisabled, isEditMode]);

  const allowedScreensArray = useAppSelector(selectAllowedScreens);
  useEffect(() => {
    setAllowedScreens(new Set(allowedScreensArray));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedScreensArray]);

  const isScreenAllowed = (screenName: string) => allowedScreens.has(screenName);

  return (
    <div className="status-header">
      <Container fluid>
        <div className="user-info d-flex align-items-center justify-content-between">
          <div className="status-header__info d-inline-flex">
            <a className="me-4 text-white text-decoration-none" href="/">
              <h3>{process.env.REACT_APP_APP_NAME}</h3>
            </a>
          </div>
          <div className="status-header__actions">
            <div className="d-inline-flex align-items-center">
              <div className="me-4">
                {t('Status_Header.login_time')}:&nbsp;
                <span className="status-header__value">{employeeInfo?.last_time_login}</span>
              </div>
              {/* <div className="me-4">
                {t('Status_Header.company_name')}:&nbsp;
                <span className="status-header__value">{employeeJobInfo?.company_name}</span>
              </div> */}
              <div className="me-4">
                {t('Status_Header.department_name')}:&nbsp;
                <span className="status-header__value">{employeeJobInfo?.department_name}</span>
              </div>
              {/* <div className="me-4">
                {t('Status_Header.job_name')}:&nbsp;
                <span className="status-header__value">
                  {employeeJobInfo?.job_id ? employeeJobInfo?.job_name : '-'}
                </span>
              </div> */}
              <div className="me-4">
                {t('Status_Header.employee_name')}:&nbsp;
                <span className="status-header__value">{employeeInfo?.employee_name}</span>
              </div>
              {/* <div className="me-4">
                {t('Status_Header.employee_code')}:&nbsp;
                <span className="status-header__value">{employeeInfo?.employee_cd}</span>
              </div> */}

              <Button variant="light" onClick={handleActionClick}>
                {actionName === 'back' ? t('Status_Header.return') : t('Status_Header.logout')}
              </Button>
            </div>
          </div>
        </div>
        <EditModeHeader />
        <div className="main-menu">
          <ul>
            <li>
              <span
                className={clsx({ active: location.pathname.startsWith('/business-type') })}
                style={{
                  pointerEvents: isLinkDisabled ? 'none' : 'auto',
                  color: isLinkDisabled ? 'grey' : 'inherit',
                }}
              >
                {t('Status_Header.business_type')}
              </span>
              <ul>
                <li>
                  <Link
                    to="/business-type/bar-guide-list"
                    className={clsx({ active: location.pathname === '/business-type/bar-guide-list' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.bar_guide_list')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/display-control-group"
                    className={clsx({ active: location.pathname === '/business-type/display-control-group' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.display_control_group')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/overlay-function"
                    className={clsx({ active: location.pathname === '/business-type/overlay-function' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.overlay_function')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/daily-menu"
                    className={clsx({ active: location.pathname === '/business-type/daily-menu' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.daily_menu')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/course-order-limit"
                    className={clsx({ active: location.pathname === '/business-type/course-order-limit' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.course_order_limit')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/service-menu"
                    className={clsx({ active: location.pathname === '/business-type/service-menu' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.service_menu')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/video-function"
                    className={clsx({ active: location.pathname === '/business-type/video-function' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.video_function')}
                  </Link>
                </li>
                <li className={clsx({ 'd-none': !isScreenAllowed(SCREEN_BUSSINESS_TYPE.SHABU_SUKIYAKI) })}>
                  <Link
                    to="/business-type/shabu-sukiyaki"
                    className={clsx({ active: location.pathname === '/business-type/shabu-sukiyaki' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.shabu_sukiyaki')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/menu-serving-time"
                    className={clsx({ active: location.pathname === '/business-type/menu-serving-time' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_serving_time')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/default-panel"
                    className={clsx({ active: location.pathname === '/business-type/default-panel' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.default_panel')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/character-setting"
                    className={clsx({ active: location.pathname === '/business-type/character-setting' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.character')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/wizard"
                    className={clsx({ active: location.pathname === '/business-type/wizard' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.wizard')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/business-type/display-timing-group"
                    className={clsx({ active: location.pathname === '/business-type/display-timing-group' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.display_timing_group')}
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <span
                className={clsx({ active: location.pathname.startsWith('/store') })}
                style={{
                  pointerEvents: isLinkDisabled ? 'none' : 'auto',
                  color: isLinkDisabled ? 'grey' : 'inherit',
                }}
              >
                {t('Status_Header.group_store')}
              </span>
              <ul>
                <li>
                  <Link
                    to="/store/store-setting"
                    className={clsx({ active: location.pathname === '/store/store-setting' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_store_setting')}
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <span
                className={clsx({ active: location.pathname.startsWith('/counter-pattern') })}
                style={{
                  pointerEvents: isLinkDisabled ? 'none' : 'auto',
                  color: isLinkDisabled ? 'grey' : 'inherit',
                }}
              >
                {t('Status_Header.group_counter_pattern')}
              </span>
              <ul>
                <li>
                  <Link
                    to="/counter-pattern/display-setting-by-counter-pattern"
                    className={clsx({
                      active: location.pathname === '/counter-pattern/display-setting-by-counter-pattern',
                    })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_display_setting_by_counter_pattern')}
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <span
                className={clsx({ active: location.pathname.startsWith('/sale-type') })}
                style={{
                  pointerEvents: isLinkDisabled ? 'none' : 'auto',
                  color: isLinkDisabled ? 'grey' : 'inherit',
                }}
              >
                {t('Status_Header.group_sale_type')}
              </span>
              <ul>
                <li>
                  <Link
                    to="/sale-type/sale-type-list"
                    className={clsx({ active: location.pathname === '/sale-type/sale-type-list' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_sale_type_list')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/sale-type/eating-way"
                    className={clsx({ active: location.pathname === '/sale-type/eating-way' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.eating_way_list')}
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <span
                className={clsx({ active: location.pathname.startsWith('/menu-book') })}
                style={{
                  pointerEvents: isLinkDisabled ? 'none' : 'auto',
                  color: isLinkDisabled ? 'grey' : 'inherit',
                }}
              >
                {t('Status_Header.group_menu_book')}
              </span>
              <ul>
                <li>
                  <Link
                    to="/menu-book/menu-book-list"
                    className={clsx({ active: location.pathname === '/menu-book/menu-book-list' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_menu_book_list')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/menu-book/genre"
                    className={clsx({ active: location.pathname === '/menu-book/genre' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_genre_list')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/menu-book/sub-genre"
                    className={clsx({ active: location.pathname === '/menu-book/sub-genre' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_subgenre_list')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/menu-book/fair-top"
                    className={clsx({ active: location.pathname === '/menu-book/fair-top' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.fair_top_list')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/menu-book/menu-list"
                    className={clsx({ active: location.pathname === '/menu-book/menu-list' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_menu_list')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/menu-book/subtitle-menu-list"
                    className={clsx({ active: location.pathname === '/menu-book/subtitle-menu-list' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.subtitle_menu_list')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/menu-book/sub-menu-group-list"
                    className={clsx({ active: location.pathname === '/menu-book/sub-menu-group-list' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_group_option_list')}
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <span
                className={clsx({ active: location.pathname.startsWith('/delivery') })}
                style={{
                  pointerEvents: isLinkDisabled || isEditMode ? 'none' : 'auto',
                  color: isLinkDisabled || isEditMode ? 'grey' : 'inherit',
                }}
              >
                {t('Status_Header.group_delivery')}
              </span>
              <ul>
                <li>
                  <Link
                    to="/delivery/delivery-setting"
                    className={clsx({ active: location.pathname === '/delivery/delivery-setting' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isEditMode ? 'none' : 'auto',
                      color: isEditMode ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_delivery_setting')}
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <span
                className={clsx({ active: location.pathname.startsWith('/data-setting') })}
                style={{
                  pointerEvents: isLinkDisabled ? 'none' : 'auto',
                  color: isLinkDisabled ? 'grey' : 'inherit',
                }}
              >
                {t('Status_Header.group_data')}
              </span>
              <ul>
                <li>
                  <Link
                    to="/data-setting/import-data"
                    className={clsx({ active: location.pathname === '/data-setting/import-data' })}
                    onClick={handleClick}
                    style={{
                      pointerEvents: isLinkDisabled ? 'none' : 'auto',
                      color: isLinkDisabled ? 'grey' : 'inherit',
                    }}
                  >
                    {t('Status_Header.menu_import_register_data')}
                  </Link>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </Container>
    </div>
  );
}

export default StatusHeader;
